import LanguageToggle from "@/components/ui/language-toggle";
import ThemeToggle from "@/components/ui/theme-toggle";
import useTheme from "@/contexts/theme/theme.context-hook";
import { signInStaffWithGoogle } from "@/services/staff.services";
import { Alert, AlertTitle, Button } from "@EspaceParkQC/sdk-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/** Firebase codes that mean the person closed or replaced the Google popup: nothing to show. */
const DISMISSED_CODES = new Set(["auth/popup-closed-by-user", "auth/cancelled-popup-request", "auth/user-cancelled"]);

/** Sign-in with the clicknpark-staff project. Its blocking function refuses anyone without staff access. */
export default function Auth(): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.resolvedTheme === "dark";
  const { t } = useTranslation("auth");
  const [errorKey, setErrorKey] = useState<string | undefined>();

  async function signIn(): Promise<void> {
    setErrorKey(undefined);

    try {
      await signInStaffWithGoogle();
      window.location.href = `${window.location.origin}/`;
    } catch (error) {
      const code = String((error as { code?: unknown })?.code ?? "");
      const message = String((error as { message?: unknown })?.message ?? "");
      if (DISMISSED_CODES.has(code)) return;

      if (code === "auth/user-disabled" || message.includes("deactivated")) setErrorKey("login.staffDeactivated");
      else if (message.includes("Google Workspace")) setErrorKey("login.useWorkspaceAccount");
      else if (code === "auth/admin-restricted-operation" || message.includes("no staff access")) setErrorKey("login.noStaffAccess");
      else setErrorKey("login.signInFailed");
    }
  }

  return (
    <div className="w-full h-screen flex lg:flex-row flex-col justify-between bg-background relative overflow-hidden">
      <div className="bg-background w-full lg:w-[600px] flex-1 max-w-full flex flex-col justify-center items-center px-10 relative z-10">
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <LanguageToggle />
          <ThemeToggle />
        </div>
        <img className="mb-10 mx-auto w-[250px] cursor-pointer" src={`/images/layout/logo-${isDark ? "dark" : "light"}.svg`} alt="clicknpark" />

        {errorKey && (
          <Alert variant="destructive">
            <AlertTitle>{t(errorKey)}</AlertTitle>
          </Alert>
        )}

        <Button size="lg" onClick={() => void signIn()}>
          <span>{t("login.signInWithGoogle")}</span>
        </Button>
      </div>
    </div>
  );
}
